import { useEffect } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { showToast } from '@quickmd-llc/design-system'
import { z } from 'zod'
import { getGetPatientsPatientIdPharmacyOrdersOrderIdQueryKey } from 'api/generated/pharmacy-fulfillment/pharmacy-fulfillment'
import { getGetPharmacyOrdersQueryKey } from 'api/generated/pharmacy-order/pharmacy-order'
import {
  clearFulfillmentAttempt,
  recordFailedAttempt,
  recordRetryingAttempt,
  registerAttemptQueryDefaults,
} from './fulfillmentAttempts'
import {
  ORDER_FULFILLMENT_DOM_EVENT,
  OrderFulfillmentEventSchema,
  OrderFulfillmentMqttEvent,
  isFinalFailure,
} from './orderFulfillmentEvents'
import { isScriptlyOrderPending, takePendingScriptlyOrder } from './pendingScriptlyOrders'

// What usePharmacyOrders stores under getGetPharmacyOrdersQueryKey(params): infinite-query data.
// Cache contents are `unknown`, so anything that is not that shape parses to "no match".
const CachedOrderPagesSchema = z.object({
  pages: z.array(z.object({ Items: z.array(z.object({ OrderId: z.string().nullish() })).nullish() })),
})

function isOrderInCachedLists(queryClient: QueryClient, orderId: string): boolean {
  return queryClient.getQueriesData({ queryKey: getGetPharmacyOrdersQueryKey() }).some(([, data]) => {
    const parsed = CachedOrderPagesSchema.safeParse(data)
    return parsed.success && parsed.data.pages.some((page) => page.Items?.some((item) => item.OrderId === orderId))
  })
}

export function useOrderFulfillmentEvents(): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    // Before any entry can arrive, including one broadcast from another tab.
    registerAttemptQueryDefaults(queryClient)

    function refreshOrder(patientId: string, orderId: string) {
      void queryClient.invalidateQueries({ queryKey: getGetPharmacyOrdersQueryKey() })
      void queryClient.invalidateQueries({
        queryKey: getGetPatientsPatientIdPharmacyOrdersOrderIdQueryKey(patientId, orderId),
      })
    }

    function handleEvent(domEvent: Event) {
      if (!(domEvent instanceof CustomEvent)) return
      const parsed = OrderFulfillmentEventSchema.safeParse(domEvent.detail)
      if (!parsed.success) return
      const event = parsed.data
      const { EventType, OrderId, PatientId } = event

      // The topic carries an event for every managed-pharmacy order, so only act on one this tab is
      // waiting on or already shows. The detail key is cheap for the rest: only an active detail
      // query refetches.
      const wasPending = isScriptlyOrderPending(OrderId)
      if (!wasPending && !isOrderInCachedLists(queryClient, OrderId)) {
        void queryClient.invalidateQueries({
          queryKey: getGetPatientsPatientIdPharmacyOrdersOrderIdQueryKey(PatientId, OrderId),
        })
        return
      }

      if (EventType === OrderFulfillmentMqttEvent.FULFILLED) {
        clearFulfillmentAttempt(queryClient, OrderId)
        refreshOrder(PatientId, OrderId)
        if (wasPending) {
          takePendingScriptlyOrder(OrderId)
          showToast({ type: 'success', message: 'Scriptly accepted the order.' })
        }
        return
      }

      if (isFinalFailure(event)) {
        const message = event.Message ?? null
        recordFailedAttempt(queryClient, OrderId, message)
        refreshOrder(PatientId, OrderId)
        if (wasPending) {
          takePendingScriptlyOrder(OrderId)
          showToast({
            type: 'error',
            message: message ? `Scriptly could not take the order: ${message}` : 'Scriptly could not take the order.',
          })
        }
        return
      }

      // A retry is scheduled, so the order is not settled: the column and the modal show the retrying
      // state, and the pending mark stays so the final outcome is the one that toasts.
      recordRetryingAttempt(queryClient, OrderId, {
        attemptNumber: event.AttemptNumber ?? null,
        nextAttemptAtUtc: event.NextAttemptAtUtc ?? null,
      })
      refreshOrder(PatientId, OrderId)
    }

    document.addEventListener(ORDER_FULFILLMENT_DOM_EVENT, handleEvent)
    return () => document.removeEventListener(ORDER_FULFILLMENT_DOM_EVENT, handleEvent)
  }, [queryClient])
}
